import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCommunityContext } from '@/providers/community-context';
import axiosInstance from '@/lib/axios';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as d3 from 'd3';
import type { AdStat, AdvertisementCampaign } from '@/types';
import useSWR from 'swr';

const PLACEMENT_LABELS: Record<string, string> = {
  DASHBOARD: 'Dashboard Banner',
  POPUP: 'Popup',
};

const CHART_COLORS = {
  views: '#3b82f6',
  clicks: '#10b981',
} as const;

function formatCurrentDateTime(date: Date) {
  return date.toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function dateKey(timestamp: string) {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export default function AdStatsPage() {
  const { community } = useCommunityContext();
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 600, height: 400 });

  const { data: campaignsData } = useSWR(
    community?.id ? `/api/communities/${community.id}/advertisement-campaigns` : null,
    (url) => axiosInstance.get(url).then((res) => res.data)
  );

  const campaign = campaignsData?.campaigns?.find(
    (c: AdvertisementCampaign) => c.id === campaignId
  );

  const campaignName = campaign
    ? `${campaign.advertisement.title} (${PLACEMENT_LABELS[campaign.type] ?? campaign.type})`
    : null;

  const totals = useMemo(() => {
    const views = stats.filter((s) => s.eventType === 'VIEW').length;
    const clicks = stats.filter((s) => s.eventType === 'CLICK').length;
    const visitorClicks = stats.filter(
      (s) => s.eventType === 'CLICK' && s.visitorId && !s.userId
    ).length;
    const visitorViews = stats.filter(
      (s) => s.eventType === 'VIEW' && s.visitorId && !s.userId
    ).length;
    return { views, clicks, visitorViews, visitorClicks };
  }, [stats]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        const { width } = entries[0].contentRect;
        const newWidth = Math.max(width, 250);
        setDimensions({ width: newWidth, height: newWidth < 500 ? 300 : 400 });
      }
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [isLoading, stats.length]);

  useEffect(() => {
    if (community?.id && campaignId) {
      axiosInstance
        .get(`/api/communities/${community.id}/advertisement-campaigns/${campaignId}/stats`)
        .then((res) => {
          setStats(res.data.stats || []);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setIsLoading(false);
        });
    }
  }, [community?.id, campaignId]);

  useEffect(() => {
    if (!svgRef.current || stats.length === 0) return;

    const viewsByDate = d3.rollup(
      stats.filter((s) => s.eventType === 'VIEW'),
      (v) => v.length,
      (d) => dateKey(d.timestamp)
    );

    const clicksByDate = d3.rollup(
      stats.filter((s) => s.eventType === 'CLICK'),
      (v) => v.length,
      (d) => dateKey(d.timestamp)
    );

    const allDateKeys = new Set([...viewsByDate.keys(), ...clicksByDate.keys()]);

    const data = Array.from(allDateKeys, (dateStr) => {
      const [year, month, day] = dateStr.split('-');
      return {
        date: new Date(Number(year), Number(month) - 1, Number(day)),
        views: viewsByDate.get(dateStr) ?? 0,
        clicks: clicksByDate.get(dateStr) ?? 0,
      };
    }).sort((a, b) => a.date.getTime() - b.date.getTime());

    const margin = { top: 20, right: 30, bottom: 50, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll('*').remove();

    const totalWidth = width + margin.left + margin.right;
    const totalHeight = height + margin.top + margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr('width', totalWidth)
      .attr('height', totalHeight)
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('max-width', '100%')
      .style('height', 'auto')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    let xDomain = d3.extent(data, (d) => d.date) as [Date, Date];
    if (xDomain[0] && xDomain[1] && xDomain[0].getTime() === xDomain[1].getTime()) {
      xDomain = [
        new Date(xDomain[0].getTime() - 86400000),
        new Date(xDomain[1].getTime() + 86400000),
      ];
    } else if (!xDomain[0] || !xDomain[1]) {
      xDomain = [new Date(), new Date()];
    }

    const x = d3.scaleTime().domain(xDomain).range([0, width]);

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(width < 400 ? 3 : 5))
      .selectAll('text')
      .attr('fill', 'currentColor');

    svg.selectAll('.domain, .tick line').attr('stroke', 'currentColor');

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 10)
      .attr('text-anchor', 'middle')
      .attr('fill', 'currentColor')
      .attr('font-size', '14px')
      .attr('font-weight', '500')
      .text('Date');

    const maxValue = d3.max(data, (d) => Math.max(d.views, d.clicks)) || 0;
    const yDomain = [0, maxValue === 0 ? 10 : maxValue + Math.ceil(maxValue * 0.2)];

    const y = d3.scaleLinear().domain(yDomain).range([height, 0]);

    svg.append('g').call(d3.axisLeft(y).ticks(5)).selectAll('text').attr('fill', 'currentColor');

    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left + 15)
      .attr('x', 0 - height / 2)
      .attr('text-anchor', 'middle')
      .attr('fill', 'currentColor')
      .attr('font-size', '14px')
      .attr('font-weight', '500')
      .text('Count');

    const line = d3
      .line<{ date: Date; views: number; clicks: number }>()
      .x((d) => x(d.date))
      .curve(d3.curveMonotoneX);

    const series = [
      { key: 'views' as const, color: CHART_COLORS.views, label: 'Views' },
      { key: 'clicks' as const, color: CHART_COLORS.clicks, label: 'Clicks' },
    ];

    for (const s of series) {
      svg
        .append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', s.color)
        .attr('stroke-width', 2.5)
        .attr(
          'd',
          line.y((d) => y(d[s.key]))
        );

      svg
        .selectAll(`circle.${s.key}`)
        .data(data)
        .enter()
        .append('circle')
        .attr('class', s.key)
        .attr('cx', (d) => x(d.date))
        .attr('cy', (d) => y(d[s.key]))
        .attr('r', 4)
        .attr('fill', s.color)
        .attr('stroke', 'hsl(var(--background))')
        .attr('stroke-width', 2);
    }

    const legend = svg.append('g').attr('transform', `translate(${width - 120}, 0)`);
    series.forEach((s, i) => {
      const g = legend.append('g').attr('transform', `translate(0, ${i * 22})`);
      g.append('line')
        .attr('x1', 0)
        .attr('x2', 18)
        .attr('y1', 8)
        .attr('y2', 8)
        .attr('stroke', s.color)
        .attr('stroke-width', 2.5);
      g.append('text')
        .attr('x', 24)
        .attr('y', 12)
        .attr('fill', 'currentColor')
        .attr('font-size', '12px')
        .text(s.label);
    });
  }, [stats, dimensions]);

  if (isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>
      <Card className="w-full border-0 bg-card shadow-none md:shadow-2xl">
        <CardHeader className="px-3 md:px-6">
          <CardTitle className="text-2xl font-black tracking-tight">Campaign Statistics</CardTitle>
          {campaignName && (
            <p className="text-base font-semibold text-foreground mt-1">{campaignName}</p>
          )}
          <CardDescription className="flex flex-col gap-1 mt-2">
            <span>Views and clicks per day over the selected time period</span>
            <span className="text-xs tabular-nums">
              Current date &amp; time: {formatCurrentDateTime(now)}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 md:px-6 flex flex-col items-center gap-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
            <div className="rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-center">
              <p className="text-2xl font-bold tabular-nums">{totals.views}</p>
              <p className="text-xs text-muted-foreground">Total views</p>
            </div>
            <div className="rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-center">
              <p className="text-2xl font-bold tabular-nums">{totals.clicks}</p>
              <p className="text-xs text-muted-foreground">Total clicks</p>
            </div>
            <div className="rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-center">
              <p className="text-2xl font-bold tabular-nums">{totals.visitorViews}</p>
              <p className="text-xs text-muted-foreground">Visitor views</p>
            </div>
            <div className="rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-center">
              <p className="text-2xl font-bold tabular-nums">{totals.visitorClicks}</p>
              <p className="text-xs text-muted-foreground">Visitor clicks</p>
            </div>
          </div>

          {stats.length > 0 ? (
            <div ref={wrapperRef} className="w-full flex justify-center overflow-visible min-w-0">
              <svg ref={svgRef} className="max-w-full h-auto"></svg>
            </div>
          ) : (
            <div className="p-12 text-muted-foreground text-center">
              No views or clicks recorded yet.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
